import * as React from 'react'
import { Card } from 'react-bootstrap'
import { styled } from 'styled-components'
import * as domain from '@packages/domain'
import { Image } from './Image'

// Styled components
const StyledCard = styled(Card)<{ $isSelected: boolean }>`
  border: 2px solid ${props => (props.$isSelected ? 'var(--bs-primary)' : 'white')};
  transition: all 0.2s ease-in-out;
  height: 100%;
  cursor: pointer;

  box-shadow: 10px 13px 16px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: ${props => (props.$isSelected ? '#0d3c61' : '#ccc')};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`
const InfoButton = styled.div`
  align-self: flex-end;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0.5rem;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid var(--bs-secondary);
  color: var(--bs-secondary);
  font-size: 0.8rem;
  font-weight: bold;
  cursor: pointer;
  z-index: 1;
  aspect-ratio: 1;

  @media screen and (min-width: 768px) {
    border-width: 2px;
  }
`

const CardImageWrapper = styled(Card.Header)`
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  background-color: transparent;
  border: none;
  padding-top: 2.5rem;
  &:has(${InfoButton}) {
    padding-top: 0;
  }
`

const StyledImage = styled(Image)`
  margin: 0 1rem;
  @media screen and (min-width: 768px) {
    margin: 0 2.5rem;
  }
  border-radius: 50%;
  aspect-ratio: 1;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
`

const OptionTitle = styled(Card.Title)`
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 0;
`

const OptionPrice = styled(Card.Footer)`
  font-weight: bold;
  font-size: 0.9rem;
  color: #0d3c61;
  background-color: transparent;
  border-top: 1px solid #e0e0e0;
  padding: 0.75rem 1.25rem 0.75rem 3.5rem;
`

const Input = styled.input`
  position: absolute;
  bottom: 0.65rem;
  left: 0.75rem;

  width: 1.75rem;
  height: 1.75rem;

  appearance: none;
  -webkit-appearance: none;

  pointer-events: none;

  outline: 2px solid var(--bs-secondary);
  border: 3px solid var(--bs-white);

  @media screen and (min-width: 768px) {
    outline-width: 3px;
    border-width: 5px;
  }

  &:checked {
    background-color: var(--bs-secondary);
  }

  &[type='radio'],
  &[type='checkbox'] {
    border-radius: 50%;
  }

  &[type='checkbox'] {
    background-color: transparent;

    &::before {
      content: '+';
      position: absolute;
      top: 45%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: var(--bs-secondary);
      opacity: 0.3;
      font-size: 2rem;
      transition: opacity 0.2s ease-in-out;
    }

    &:checked {
      background-color: transparent;

      &::before {
        opacity: 1;
      }
    }
  }
`

export interface ViewProps {
  option: domain.Option.Type
  isSelected: boolean
  onInfoClick: () => void
  onSelect: () => void
  selectionMode: domain.Step.SelectionMode
  stepId: domain.StepId.Type
}

const View: React.FC<ViewProps> = ({ option, isSelected, onSelect, onInfoClick, selectionMode, stepId }) => {
  const optionName = option.name.replaceAll('"', '').replaceAll(/\s+/g, '-')

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering the option selection
    onInfoClick()
  }

  // Generate an ID for a11y
  const inputId = `option-${String(stepId)}-${optionName}`

  return (
    <StyledCard $isSelected={isSelected} onClick={onSelect}>
      <Input
        type={selectionMode === 'single' ? 'radio' : 'checkbox'}
        id={inputId}
        name={`step-${String(stepId)}`}
        value={optionName}
        checked={isSelected}
        onChange={onSelect}
        aria-label={optionName}
      />

      <CardImageWrapper>
        <InfoButton onClick={handleInfoClick}>i</InfoButton>
        <StyledImage imageId={option.image} alt={optionName} />
      </CardImageWrapper>
      <Card.Body>
        <OptionTitle>{option.name}</OptionTitle>
      </Card.Body>
      <OptionPrice>€ {option.price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</OptionPrice>
    </StyledCard>
  )
}

export type OptionNoneProps = Omit<ViewProps, 'onInfoClick'>

const OptionNone: React.FC<OptionNoneProps> = ({ option, isSelected, onSelect, stepId }) => {
  const optionName = option.name.replaceAll('"', '').replaceAll(/\s+/g, '-')
  // Generate an ID for a11y
  const inputId = `option-${String(stepId)}-${optionName}`

  return (
    <StyledCard $isSelected={isSelected} onClick={onSelect}>
      <Input
        type="radio"
        id={inputId}
        name={`step-${String(stepId)}`}
        value={optionName}
        checked={isSelected}
        onChange={onSelect}
        aria-label={optionName}
      />

      <CardImageWrapper>
        <StyledImage imageId={option.image} alt={optionName} />
      </CardImageWrapper>
      <Card.Body>
        <OptionTitle>{option.name}</OptionTitle>
      </Card.Body>
      <OptionPrice>€ {option.price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</OptionPrice>
    </StyledCard>
  )
}

export const Option = Object.assign(View, { OptionNone })
